
import React from 'react';

interface CurrencySelectorProps {
  selectedCurrency: string;
  onSelectCurrency: (currency: string) => void;
  convertedAmount: number | null;
  baseAmount: number;
  isLoading: boolean;
}

const currencies = [
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', flag: '🇳🇬' },
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
  { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', flag: '🇨🇦' },
  { code: 'GHS', symbol: 'GH₵', name: 'Ghanaian Cedi', flag: '🇬🇭' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling', flag: '🇰🇪' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand', flag: '🇿🇦' },
];

export default function CurrencySelector({
  selectedCurrency,
  onSelectCurrency,
  convertedAmount,
  baseAmount,
  isLoading,
}: CurrencySelectorProps) {
  const selected = currencies.find((c) => c.code === selectedCurrency);

  const formatAmount = (amount: number, currencyCode: string) => {
    const curr = currencies.find((c) => c.code === currencyCode);
    return `${curr?.symbol || ''}${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
      <div>
        <h4 className="text-sm font-semibold text-slate-900 mb-1">Consultation Fee</h4>
        <p className="text-xs text-gray-500">Base price: ₦50,000 NGN</p>
      </div>

      <div>
        <label className="text-xs font-medium text-gray-500 mb-2 block">
          Select your preferred currency
        </label>
        <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
          {currencies.map((currency) => (
            <button
              key={currency.code}
              onClick={() => onSelectCurrency(currency.code)}
              className={`flex flex-col items-center gap-0.5 sm:gap-1 py-2 sm:py-3 px-1 sm:px-2 rounded-xl text-xs font-medium transition-all cursor-pointer
                ${selectedCurrency === currency.code
                  ? 'bg-emerald-500 text-white shadow-md shadow-emerald-200'
                  : 'bg-gray-50 text-slate-700 hover:bg-emerald-50 border border-gray-100'
                }`}
            >
              <span className="text-base sm:text-lg">{currency.flag}</span>
              <span className="text-[10px] sm:text-xs">{currency.code}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-emerald-50 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">You pay</span>
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm text-gray-500">Converting...</span>
            </div>
          ) : (
            <span className="text-2xl font-bold text-emerald-700">
              {convertedAmount !== null
                ? formatAmount(convertedAmount, selectedCurrency)
                : formatAmount(baseAmount, 'NGN')}
            </span>
          )}
        </div>
        {selectedCurrency !== 'NGN' && convertedAmount !== null && !isLoading && (
          <p className="text-xs text-gray-500 mt-1 text-right">
            ≈ ₦{baseAmount.toLocaleString()} NGN
          </p>
        )}
        <p className="text-xs text-gray-400 mt-2">
          {selected?.flag} {selected?.name} • Rate updates in real-time
        </p>
      </div>
    </div>
  );
}
